import React, { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import api from 'services/api';
import DicaDeCampo from 'components/common/DicaDeCampo';

/**
 * O que a cobrança recuperou nos 12 meses que terminam no mês escolhido, do time ou de um cobrador.
 *
 * É a recuperação pelo financeiro: títulos pagos que passaram pela cobrança, separados por como o
 * pagamento caiu. Não depende de quem finalizou a OS, porque o sufixo "(COB - INT)" só entrou no
 * cadastro recentemente e esconderia os meses anteriores. A comissão continua na tela, mês a mês.
 *
 * Cada mês é uma leitura do IXC de alguns segundos, então os meses chegam um a um, do mais recente
 * para o mais antigo, pelo modo `resumo=1` da mesma rota da tela (mês fechado fica 6 h em cache).
 */

const ROTULO_MES = (mes) => new Date(`${mes}-01T00:00:00`).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }).replace('. de ', '/').replace('.', '');
// "Novembro de 2025": só a primeira letra; `capitalize` do CSS faria "Novembro De 2025".
const MES_POR_EXTENSO = (mes) => {
  const t = new Date(`${mes}-01T00:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const reais = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const milhar = (valor) => {
  const v = Number(valor || 0);
  return v >= 1000 ? `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : `R$ ${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};
const inteiro = (valor) => Number(valor || 0).toLocaleString('pt-BR');

export const ultimosMeses = (fim, quantos = 12) => {
  const [ano, mes] = fim.split('-').map(Number);
  return Array.from({ length: quantos }, (_, i) => {
    const d = new Date(ano, mes - 1 - (quantos - 1 - i), 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
};

// Ordem de empilhamento = ordem dos tokens --serie-*, validada assim (ver src/index.css).
export const GRUPOS = [
  { chave: 'interna', rotulo: 'Com OS de cobrança interna aberta', cor: 'var(--serie-1)' },
  { chave: 'apos_os', rotulo: 'Depois de a OS fechar', cor: 'var(--serie-2)' },
  { chave: 'externa', rotulo: 'Durante a cobrança externa', cor: 'var(--serie-3)' },
];

const foco = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]';

// `resumoAtual` é o do mês escolhido, que a tela já leu: o gráfico não pede de novo.
const SerieDaComissao = ({ mes, cobradorId, resumoAtual, onEscolherMes, primeiroMes, grupoSelecionado, onEscolherGrupo }) => {
  const [buscados, setBuscados] = useState({});
  const [falhas, setFalhas] = useState(0);
  const [tabela, setTabela] = useState(false);
  const meses = useMemo(() => ultimosMeses(mes), [mes]);

  useEffect(() => {
    let vigente = true;
    setBuscados({});
    setFalhas(0);
    (async () => {
      // Do mais recente para o mais antigo, um por vez: é o que interessa primeiro, e o IXC agradece.
      for (const m of [...meses].reverse().filter((m) => m !== meses[meses.length - 1])) {
        try {
          const r = await api.get(`/cobranca/comissao/${m}`, { params: { resumo: 1, ...(cobradorId ? { cobrador: cobradorId } : {}) } });
          if (!vigente) return;
          setBuscados((atual) => ({ ...atual, [m]: r.data?.data?.resumo }));
        } catch {
          if (!vigente) return;
          setFalhas((f) => f + 1);
        }
      }
    })();
    return () => { vigente = false; };
  }, [meses, cobradorId]);

  const resumos = useMemo(() => (resumoAtual ? { ...buscados, [mes]: resumoAtual } : buscados), [buscados, resumoAtual, mes]);
  const dados = meses.map((m) => {
    const rec = resumos[m]?.recuperacao;
    return {
      mes: m,
      rotulo: ROTULO_MES(m),
      rec,
      // O gráfico é em R$; a quantidade de títulos está na dica de cada mês e em "Ver os números".
      ...Object.fromEntries(GRUPOS.map((g) => [g.chave, rec?.[g.chave]?.valor || 0])),
    };
  });
  const carregados = Object.keys(resumos).length;
  const pendentes = meses.length - carregados - falhas;
  const clicavel = (ponto) => ponto && ponto.mes >= primeiroMes && ponto.mes !== mes;
  const ultimoGrupo = (ponto) => [...GRUPOS].reverse().find((g) => ponto[g.chave] > 0)?.chave;

  return (
    <section className="min-w-0 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] p-5" aria-labelledby="titulo-serie">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <h3 id="titulo-serie" className="text-sm font-semibold text-[color:var(--text-primary)]">Recuperação nos últimos 12 meses</h3>
            <DicaDeCampo id="dica-recuperacao" titulo="a recuperação" alinhamento="esquerda">
              <span className="block font-semibold text-[color:var(--text-primary)]">Como a recuperação é calculada</span>
              <span className="mt-1 block">
                Soma o valor pago dos títulos que passaram pela cobrança: pagos com atraso, de clientes que tiveram
                uma OS de cobrança (interna ou externa) aberta entre o vencimento e o pagamento. Conta pelo mês do
                pagamento, direto do financeiro do IXC.
              </span>
              <span className="mt-1 block">Cada pagamento cai num grupo, pelo que estava aberto na hora em que o cliente pagou:</span>
              <span className="mt-1 block"><b className="font-medium text-[color:var(--text-primary)]">Com OS de cobrança interna aberta:</b> OS 594 ou 595 do contrato, aberta depois do vencimento.</span>
              <span className="block"><b className="font-medium text-[color:var(--text-primary)]">Depois de a OS fechar:</b> passou pela cobrança, mas pagou com a OS já finalizada.</span>
              <span className="block"><b className="font-medium text-[color:var(--text-primary)]">Durante a cobrança externa:</b> OS 596 ou 597 aberta no pagamento.</span>
              <span className="mt-1 block">
                Não depende de quem finalizou a OS nem de a OS ter sido assumida: é a recuperação do processo, não a
                comissão. No recorte de um cobrador, entram as OS dele.
              </span>
            </DicaDeCampo>
          </div>
          <p className="text-xs text-[color:var(--text-muted)]" aria-live="polite">
            {pendentes > 0 ? `Calculando no IXC: ${carregados} de ${meses.length} meses prontos` : 'Títulos pagos que passaram pela cobrança, por mês de pagamento'}
            {falhas > 0 && ` · ${falhas} ${falhas === 1 ? 'mês não respondeu' : 'meses não responderam'}; atualize para tentar de novo`}
          </p>
        </div>
      </div>

      <div className="mt-4 h-60" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dados} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="28%">
            <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="rotulo" tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={6} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} tickFormatter={milhar} width={88} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
            <Tooltip content={<Dica />} cursor={{ fill: 'var(--surface-muted)' }} />
            {GRUPOS.map((g) => (
              <Bar
                key={g.chave}
                dataKey={g.chave}
                name={g.rotulo}
                stackId="mes"
                fill={g.cor}
                stroke="var(--surface-strong)"
                strokeWidth={1}
                maxBarSize={36}
                // Os meses chegam um a um: com animação, cada chegada redesenharia todas as barras.
                isAnimationActive={false}
                onClick={(ponto) => clicavel(ponto) && onEscolherMes(ponto.mes)}
              >
                {dados.map((ponto) => (
                  <Cell key={ponto.mes} radius={ultimoGrupo(ponto) === g.chave ? [4, 4, 0, 0] : 0} cursor={clicavel(ponto) ? 'pointer' : 'default'} />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <ul className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[color:var(--text-muted)]">
          {GRUPOS.map((g) => (
            <li key={g.chave}>
              <button type="button" aria-pressed={grupoSelecionado === g.chave} onClick={() => onEscolherGrupo?.(g.chave)} className={`inline-flex min-h-9 items-center gap-1.5 rounded px-2 ${foco} hover:bg-[color:var(--surface-muted)] ${grupoSelecionado === g.chave ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] underline underline-offset-4' : ''}`} >
              <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: g.cor }} aria-hidden="true" />
              {g.rotulo}
              </button>
            </li>
          ))}
        </ul>
        <button type="button" aria-expanded={tabela} onClick={() => setTabela((v) => !v)} className={`rounded text-xs font-medium text-[color:var(--text-primary)] underline underline-offset-4 ${foco}`}>
          {tabela ? 'Esconder os números' : 'Ver os números'}
        </button>
      </div>

      {/* Visível sob demanda: uma das cores fica abaixo de 3:1 no claro, e o número não pode depender dela. */}
      <div className={tabela ? 'mt-3 overflow-x-auto' : 'sr-only'}>
        <table className="w-full text-xs tabular-nums">
          <caption className="sr-only">Recuperação por mês de pagamento, últimos 12 meses</caption>
          <thead className="text-left text-[color:var(--text-muted)]">
            <tr>
              <th scope="col" className="py-1.5 pr-3 font-medium">Mês</th>
              {GRUPOS.map((g) => <th key={g.chave} scope="col" className="py-1.5 pr-3 text-right font-medium">{g.rotulo}</th>)}
              <th scope="col" className="py-1.5 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border)] text-[color:var(--text-primary)]">
            {dados.map((p) => (
              <tr key={p.mes}>
                <th scope="row" className="py-1.5 pr-3 text-left font-medium">{MES_POR_EXTENSO(p.mes)}</th>
                {GRUPOS.map((g) => (
                  <td key={g.chave} className="py-1.5 pr-3 text-right">
                    {p.rec ? `${reais(p.rec[g.chave].valor)} · ${inteiro(p.rec[g.chave].quantidade)}` : 'carregando'}
                  </td>
                ))}
                <td className="py-1.5 text-right font-semibold">
                  {p.rec ? reais(GRUPOS.reduce((t, g) => t + p.rec[g.chave].valor, 0)) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const Dica = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (!p.rec) {
    return <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2 text-xs text-[color:var(--text-muted)] shadow-lg">Calculando…</div>;
  }
  const total = GRUPOS.reduce((t, g) => ({ valor: t.valor + p.rec[g.chave].valor, quantidade: t.quantidade + p.rec[g.chave].quantidade }), { valor: 0, quantidade: 0 });
  return (
    <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-[color:var(--text-primary)]">{MES_POR_EXTENSO(p.mes)}</p>
      <dl className="mt-1 grid grid-cols-[auto_auto_auto] gap-x-3 gap-y-0.5 tabular-nums">
        {GRUPOS.map((g) => (
          <React.Fragment key={g.chave}>
            <dt className="inline-flex items-center gap-1.5 text-[color:var(--text-muted)]"><span className="h-2 w-2 rounded-sm" style={{ backgroundColor: g.cor }} aria-hidden="true" />{g.rotulo}</dt>
            <dd className="text-right text-[color:var(--text-primary)]">{reais(p.rec[g.chave].valor)}</dd>
            <dd className="text-right text-[color:var(--text-muted)]">{inteiro(p.rec[g.chave].quantidade)}</dd>
          </React.Fragment>
        ))}
        <dt className="font-semibold text-[color:var(--text-primary)]">Total</dt>
        <dd className="text-right font-semibold text-[color:var(--text-primary)]">{reais(total.valor)}</dd>
        <dd className="text-right text-[color:var(--text-muted)]">{inteiro(total.quantidade)}</dd>
      </dl>
    </div>
  );
};

export default SerieDaComissao;
